import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import FormInput from "@/components/form-fields/form-input";
import AvatarText from "@/components/reusables/AvatarText";
import { CustomToolTip } from "@/components/reusables/CustomToolTip";
import FormDialog from "@/components/reusables/FormDialog";
import { SelectMulti } from "@/components/reusables/custom-form-fields/FormSelect";
import EmptyData from "@/components/state-pages/EmptyData";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import * as Table from "@/components/ui/table";
import { Button_Names } from "@/constants/global-constants";
import { PROJECT_CONSTANTS } from "@/constants/projects.constant";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import {
  IProductRequestDetail,
  ISubProduct,
  ProductType,
  SubProductStatus,
} from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";
import {
  useAssignRequestedProductToProjectMutation,
  useGetProductsQuery,
  useLazyGetSubProductListQuery,
} from "@/redux/api/toolsQuery";
import {
  TAssignRequestedProductToProject,
  assignRequestedProductToProjectSchema,
} from "@/schemas/tools.schema";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

interface AssignRequestProductToProjectProps {
  isOpen: boolean;
  toggleModal: () => void;
  data: IProductRequestDetail[];
}

const AssignRequestProductToProject = ({
  isOpen,
  toggleModal,
  data,
}: AssignRequestProductToProjectProps): JSX.Element => {
  const [subProductsOptions, setSubProductsOptions] = useState<
    Record<string, ISubProduct[]>
  >({});

  const { TOAST_MESSAGE, CONTENT_TEXT, EMPTY_MESSAGE } =
    PROJECT_CONSTANTS.ASSIGN_TOOLS_CONSTANTS;
  const { id, reset } = useQueryID(ToolsKeys.PRODUCT_REQUEST_ID);

  const [assignRequestedProductToProject, { isLoading: isAssigning }] =
    useAssignRequestedProductToProjectMutation();
  const { data: products, isLoading } = useGetProductsQuery(
    {
      filters: `?status=${SubProductStatus.AVAILABLE}`,
    },
    {
      skip: !isOpen || !id,
    },
  );
  const [getSubProducts] = useLazyGetSubProductListQuery();

  const handleFetchSubProducts = async (productId: string): Promise<void> => {
    if (!subProductsOptions[productId]) {
      try {
        const res = await getSubProducts({ productId }).unwrap();
        setSubProductsOptions((prev) => ({
          ...prev,
          [productId]: res?.data || [],
        }));
      } catch (err) {
        customToast.error("Failed to fetch subproducts");
      }
    }
  };

  const availableTools = products?.data;
  const alreadySelectedTools = data.map((item) => item.productId);

  const form = useForm<TAssignRequestedProductToProject>({
    resolver: zodResolver(assignRequestedProductToProjectSchema),
    ...(availableTools?.length && {
      values: {
        items: availableTools
          ?.map((item) => ({
            productId: item?.id,
            quantity:
              data?.find((product) => product.productId === item.id)
                ?.quantity ?? 0,
            type: item.type,
            description: undefined,
            note: undefined,
            isSelected: alreadySelectedTools.includes(item.id),
            subProductIds: [],
          }))
          .sort((a, b) => Number(b.isSelected) - Number(a.isSelected)), // Sort selected items to the top
      },
    }),
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "items",
    keyName: "idx",
  });

  const handleExit = (): void => {
    form.reset();
    reset();
    toggleModal();
  };

  const onSubmit = async (
    value: TAssignRequestedProductToProject,
  ): Promise<void> => {
    const response = await assignRequestedProductToProject({
      body: value?.items?.filter((item) => item.isSelected),
      id,
    });

    validateResponse({
      response,
      onSuccess: () => {
        handleExit();
        customToast.success(TOAST_MESSAGE.PRODUCT_ASSIGNED);
      },
    });
  };

  const CustomTableHeader = (): JSX.Element => {
    const commonClasses = "capitalize  border-r border-r-white text-primary ";

    const isAllSelected = form
      .getValues("items")
      .every((item) => item.isSelected);

    return (
      <Table.TableHeader className={"bg-[#E4E8F2]"}>
        <Table.TableRow>
          <Table.TableHead
            className={cn(commonClasses, "border-r-transparent")}
          >
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={(value) => {
                form.setValue(
                  "items",
                  form.watch("items").map((item) => ({
                    ...item,
                    isSelected: Boolean(value),
                  })),
                );
              }}
              className=' mt-1'
            />
          </Table.TableHead>
          <Table.TableHead className={cn(commonClasses)}>Name</Table.TableHead>
          <Table.TableHead className={commonClasses}>Quantity</Table.TableHead>
          <Table.TableHead className={commonClasses}>
            Sub Products
          </Table.TableHead>
          <Table.TableHead className={commonClasses}>Notes</Table.TableHead>
          <Table.TableHead className={commonClasses}>Comments</Table.TableHead>
        </Table.TableRow>
      </Table.TableHeader>
    );
  };

  return (
    <FormDialog
      isOpen={isOpen}
      toggleModal={handleExit}
      isLoading={isLoading}
      title={CONTENT_TEXT.ASSIGN_TO_PROJECT}
      className='md:max-w-7xl'
      isFormDirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
          <ScrollArea className='h-[400px] sidebar_scrollbar'>
            <div className='rounded-md border overflow-clip'>
              <Table.Table>
                <CustomTableHeader />
                <Table.TableBody>
                  {fields.length ? (
                    fields?.map((field, index) => {
                      const commonCellClassName = "border-r border-r-gray-100";
                      const isRowSelected = form.watch(
                        `items.${index}.isSelected`,
                      );

                      const product = availableTools?.find(
                        (product) => product?.id === field?.productId,
                      );

                      return (
                        <Table.TableRow
                          key={field.idx}
                          className='even:bg-transparent hover:bg-transparent'
                        >
                          <Table.TableCell>
                            <Checkbox
                              checked={form.getValues(
                                `items.${index}.isSelected`,
                              )}
                              onCheckedChange={(value) => {
                                form.setValue(
                                  `items.${index}.isSelected`,
                                  Boolean(value),
                                );
                              }}
                            />
                          </Table.TableCell>
                          <Table.TableCell className={cn(commonCellClassName)}>
                            <AvatarText
                              src={product?.images[0] || ""}
                              name={product?.name || ""}
                              title={product?.name || ""}
                            />
                          </Table.TableCell>
                          <Table.TableCell className={cn(commonCellClassName)}>
                            <FormInput
                              name={`items.${index}.quantity`}
                              form={form}
                              input={{
                                type: "number",
                                placeholder: "Enter quantity",
                                disabled: !isRowSelected,
                              }}
                            />
                          </Table.TableCell>
                          <Table.TableCell
                            className={cn(
                              commonCellClassName,
                              "min-w-72 max-w-72",
                            )}
                          >
                            {product?.type === ProductType.Has_Quantity ? (
                              <div className='pl-3 '>--</div>
                            ) : (
                              <>
                                <SelectMulti
                                  disabled={!isRowSelected}
                                  isLoading={
                                    !subProductsOptions[field.productId]
                                  }
                                  onFocus={async () => {
                                    await handleFetchSubProducts(
                                      field.productId,
                                    );
                                  }}
                                  className='mt-2'
                                  maxSelection={
                                    form.watch(`items.${index}.quantity`) ??
                                    undefined
                                  }
                                  withoutForm={{
                                    values:
                                      form.getValues(
                                        `items.${index}.subProductIds`,
                                      ) ?? [],
                                    onValueChange: (values) => {
                                      const selectedIds = values.map(
                                        (value) => value.value,
                                      );
                                      form.setValue(
                                        `items.${index}.subProductIds`,
                                        selectedIds,
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        },
                                      );
                                    },
                                  }}
                                  options={(
                                    subProductsOptions[field.productId] || []
                                  ).map((subProduct) => ({
                                    label: subProduct.name,
                                    value: subProduct.id,
                                    customNode: (
                                      <CustomToolTip
                                        toolTipTrigger={
                                          <AlertCircle className='h-4 w-4 text-red-500' />
                                        }
                                        toolTipContent={
                                          ToolsConstants.PRODUCTS.ITEMS_CONSUMED
                                        }
                                      />
                                    ),
                                  }))}
                                  isRequired
                                />
                                <span className='text-destructive text-xs'>
                                  {
                                    form.formState.errors?.items?.[index]
                                      ?.subProductIds?.message
                                  }
                                </span>
                              </>
                            )}
                          </Table.TableCell>
                          <Table.TableCell className={cn(commonCellClassName)}>
                            <FormInput
                              name={`items.${index}.note`}
                              form={form}
                              input={{
                                disabled: !isRowSelected,
                              }}
                            />
                          </Table.TableCell>
                          <Table.TableCell className={cn(commonCellClassName)}>
                            <FormInput
                              hideError
                              name={`items.${index}.description`}
                              form={form}
                              input={{
                                disabled: !isRowSelected,
                              }}
                            />
                          </Table.TableCell>
                        </Table.TableRow>
                      );
                    })
                  ) : (
                    <Table.TableRow>
                      <Table.TableCell colSpan={7}>
                        <EmptyData
                          title={CONTENT_TEXT.TOOLS}
                          subtitle={EMPTY_MESSAGE.NO_TOOLS_AVAILABLE}
                        />
                      </Table.TableCell>
                    </Table.TableRow>
                  )}
                </Table.TableBody>
              </Table.Table>
            </div>
          </ScrollArea>
          <div className=' flex justify-between '>
            <Button
              variant={"ghost"}
              onClick={() => form.reset()}
              disabled={isAssigning}
            >
              {Button_Names.Reset}
            </Button>
            <Button
              className='w-[300px]'
              type='submit'
              isLoading={isAssigning}
              disabled={
                !form.getValues().items?.some((item) => item.isSelected)
              }
            >
              {CONTENT_TEXT.ASSIGN_TOOLS}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default AssignRequestProductToProject;
